import { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandingPage = () => {
  const { studio, setStudio } = useOutletContext();
  const { getAuthHeader } = useAuth();
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: studio?.name || '',
    description: studio?.description || '',
    logo_url: studio?.logo_url || '',
    accent_color: studio?.accent_color || '#FFFFFF',
    phone: studio?.phone || '',
    email: studio?.email || '',
    address: studio?.address || '',
    social_links: studio?.social_links || {}
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/studios/me`, formData, { headers: getAuthHeader() });
      setStudio(response.data);
      toast.success('Branding updated');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSocialLinkChange = (platform, value) => {
    setFormData({
      ...formData,
      social_links: { ...formData.social_links, [platform]: value }
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="branding-page"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Branding</h1>
          <p className="text-white/60">Customize your studio's look and contact info</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-[#FFFFFF] text-black hover:bg-[#E0E0E0]"
          data-testid="save-branding-btn"
        >
          {saving ? <span className="spinner w-5 h-5"></span> : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Basic Info */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h2 className="font-semibold mb-6">Basic Info</h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white/70 mb-2 block">Studio Name</Label>
              <Input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#171717] border-white/10"
                data-testid="branding-name"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Description</Label>
              <Input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Premium vehicle wrapping studio"
                className="bg-[#171717] border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Logo URL</Label>
              <Input
                type="url"
                value={formData.logo_url}
                onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                placeholder="https://..."
                className="bg-[#171717] border-white/10"
              />
              {formData.logo_url && (
                <img src={formData.logo_url} alt="Logo preview" className="h-16 mt-2" />
              )}
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Accent Color</Label>
              <div className="flex items-center gap-4">
                <input
                  type="color"
                  value={formData.accent_color}
                  onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                  className="w-12 h-12 rounded-sm cursor-pointer border-0"
                />
                <Input
                  type="text"
                  value={formData.accent_color}
                  onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                  className="bg-[#171717] border-white/10 w-32"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6">
          <h2 className="font-semibold mb-6">Contact Info</h2>
          
          <div className="space-y-4">
            <div>
              <Label className="text-white/70 mb-2 block">Phone</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+1 234 567 890"
                className="bg-[#171717] border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="hello@studio.com"
                className="bg-[#171717] border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Address</Label>
              <Input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, Country"
                className="bg-[#171717] border-white/10"
              />
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-[#0F0F0F] border border-white/10 rounded-sm p-6 lg:col-span-2">
          <h2 className="font-semibold mb-6">Social Links</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70 mb-2 block">Instagram</Label>
              <Input
                type="url"
                value={formData.social_links.instagram || ''}
                onChange={(e) => handleSocialLinkChange('instagram', e.target.value)}
                placeholder="https://instagram.com/yourstudio"
                className="bg-[#171717] border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Facebook</Label>
              <Input
                type="url"
                value={formData.social_links.facebook || ''}
                onChange={(e) => handleSocialLinkChange('facebook', e.target.value)}
                placeholder="https://facebook.com/yourstudio"
                className="bg-[#171717] border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Twitter / X</Label>
              <Input
                type="url"
                value={formData.social_links.twitter || ''}
                onChange={(e) => handleSocialLinkChange('twitter', e.target.value)}
                placeholder="https://x.com/yourstudio"
                className="bg-[#171717] border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">YouTube</Label>
              <Input
                type="url"
                value={formData.social_links.youtube || ''}
                onChange={(e) => handleSocialLinkChange('youtube', e.target.value)}
                placeholder="https://youtube.com/@yourstudio"
                className="bg-[#171717] border-white/10"
              />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default BrandingPage;
